import PropTypes from 'prop-types';
import { forwardRef } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Card, CardContent, CardHeader, Divider, Typography, useMediaQuery } from '@mui/material';

// constant
const headerSX = {
  '& .MuiCardHeader-action': { mr: 0 }
};

// ==============================|| CUSTOM MAIN CARD ||============================== //

const MainCard = forwardRef(
  (
    {
      border = true,
      boxShadow,
      children,
      content = true,
      contentClass = '',
      contentSX = {},
      darkTitle,
      secondary,
      shadow,
      sx = {},
      title,
      ...others
    },
    ref
  ) => {
    const theme = useTheme();
    const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    return (
      <Card
        ref={ref}
        {...others}
        sx={{
          border: border ? '1px solid' : 'none',
          borderColor: 'rgba(255, 255, 255, 0.72)',
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.88) 0%, rgba(255, 255, 255, 0.68) 100%)',
          backdropFilter: 'blur(26px)',
          ':hover': {
            boxShadow: boxShadow ? shadow || '0 28px 70px rgba(15, 140, 255, 0.14), 0 14px 34px rgba(139, 92, 246, 0.12)' : 'inherit',
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease'
          },
          borderRadius: matchDownSm ? '18px' : '24px',
          boxShadow: '0 22px 70px rgba(7, 17, 31, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
          transition: 'all 0.3s ease',
          ...sx
        }}
      >
        {/* card header and action */}
        {title && (
          <CardHeader
            sx={{
              ...headerSX,
              px: matchDownSm ? 3 : 4,
              py: matchDownSm ? 2 : 3
            }}
            title={
              darkTitle ? (
                <Typography
                  variant={matchDownSm ? 'h4' : 'h3'}
                  sx={{
                    fontSize: matchDownSm ? '1.375rem' : '1.75rem',
                    lineHeight: matchDownSm ? '1.75rem' : '2.25rem',
                    fontWeight: 800,
                    letterSpacing: 0,
                    color: theme.palette.text.primary
                  }}
                >
                  {title}
                </Typography>
              ) : (
                <Typography
                  variant={matchDownSm ? 'h4' : 'h3'}
                  sx={{
                    fontSize: matchDownSm ? '1.375rem' : '1.75rem',
                    lineHeight: matchDownSm ? '1.75rem' : '2.25rem',
                    fontWeight: 800,
                    letterSpacing: 0,
                    color: theme.palette.text.primary
                  }}
                >
                  {title}
                </Typography>
              )
            }
            action={secondary}
          />
        )}

        {/* content & header divider */}
        {title && <Divider sx={{ borderColor: 'rgba(15, 140, 255, 0.12)' }} />}

        {/* card content */}
        {content && (
          <CardContent
            sx={{
              ...contentSX,
              px: matchDownSm ? 3 : 4,
              py: matchDownSm ? 3 : 4
            }}
            className={contentClass}
          >
            {children}
          </CardContent>
        )}
        {!content && children}
      </Card>
    );
  }
);

MainCard.propTypes = {
  border: PropTypes.bool,
  boxShadow: PropTypes.bool,
  children: PropTypes.node,
  content: PropTypes.bool,
  contentClass: PropTypes.string,
  contentSX: PropTypes.object,
  darkTitle: PropTypes.bool,
  secondary: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.object]),
  shadow: PropTypes.string,
  sx: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.object])
};

export default MainCard;
